import { FC, ReactNode, SyntheticEvent, useState } from 'react';
import _filter from 'lodash/filter';
import _includes from 'lodash/includes';
import _remove from 'lodash/remove';

import { UnderlinedButton } from '@components/shared/buttons/underlined-button/UnderlinedButton';
import { Collapse } from '@components/shared/collapse/Collapse';
import { ExpandPanel } from '@components/shared/ExpandPanel/ExpandPanel';
import { Checkbox } from '@components/shared/form-elements/checkbox/Checkbox';
import TreeItem from '@page-layouts/catalog/FiltersBar/TreeItem/TreeItem';
import { IFilterOptions, IFilterProductCatalogCategory, IFilterProp, ISetFiltersParam } from '@types';
import { ATTRIBUTE_CATEGORY_PATH } from './constants';
import FilterLabel from './FilterLabel';
import { MAX_CATEGORIES_OPTIONS_SHOWN } from './options-tools';

interface IProps {
  classes: { [key: string]: string };
  filters: ISetFiltersParam;
  filterOptionsData: IFilterOptions;
  setFilters: (newFilters: IFilterProp[]) => void;
  label: string;
  locale?: string;
}

const CatalogCategory: FC<IProps> = ({
  locale,
  label: labelSection,
  filters,
  setFilters,
  filterOptionsData,
  classes
}) => {
  const [allList, setAllList] = useState(false);
  const filtersFast = filters.arrayRes || [];

  if (
    !filterOptionsData.catalogCategory.length ||
    (filterOptionsData.catalogCategory.length === 1 && !filterOptionsData.catalogCategory[0].items.length)
  ) {
    return null;
  }

  // map filters on filterOptions
  let attributeOptionNodes: ReactNode[] = [];
  const attributeOptions = filterOptionsData.catalogCategory;
  const attributeFilterValues: string[] =
    _filter(filters.arrayRes, { path: 'category' }).map((filter) => filter.value) || [];

  const arraySelected = filtersFast.filter((item) => item.path === ATTRIBUTE_CATEGORY_PATH);
  const arraySelectedLength = (arraySelected && arraySelected.length) || 0;
  const arraySelectedLast = arraySelectedLength ? arraySelected[0] : null;

  const handleResetCategoryFilters = (ev: SyntheticEvent | MouseEvent) => {
    if (arraySelectedLast) {
      ev.stopPropagation();
      setFilters(filtersFast.filter((item) => item.path !== ATTRIBUTE_CATEGORY_PATH));
    }
  };

  const handleChangeCheckbox =
    (item: IFilterProductCatalogCategory) => (_event: SyntheticEvent, isChecked: boolean) => {
      const isHasChildren = item.items && item.items.length;

      if (isHasChildren) {
        prepareFilterCategoriesStateChildren(item, isChecked, filtersFast);
        return setFilters(filtersFast);
      }

      const newFilterPropValue: IFilterProp = {
        rule: 'include',
        path: 'category',
        value: item.alias,
        label: item.name
      };

      if (!isChecked) {
        _remove(filtersFast, newFilterPropValue);
      } else if (!_includes(filtersFast, newFilterPropValue)) {
        filtersFast.push(newFilterPropValue);
      }

      setFilters(filtersFast);
    };

  const getGroupState = (item: IFilterProductCatalogCategory): { isChecked: boolean; isUnchecked: boolean } => {
    let isChecked = false;
    let isUnchecked = false;
    (item.items || []).forEach((subItem) => {
      isChecked = isChecked || attributeFilterValues.indexOf(subItem.alias) > -1;
      isUnchecked = isUnchecked || attributeFilterValues.indexOf(subItem.alias) === -1;
    });

    return { isChecked, isUnchecked };
  };

  const sortByProductCount = (a: IFilterProductCatalogCategory, b: IFilterProductCatalogCategory) => {
    if (a.productsCount !== b.productsCount) {
      return b.productsCount - a.productsCount;
    } else {
      return a.name.localeCompare(b.name, locale || 'ua');
    }
  };

  const sortByIsTopLevel = (a: IFilterProductCatalogCategory, _b: IFilterProductCatalogCategory) => {
    if (a.firstLevel) {
      return -1;
    }
    return 0;
  };

  const sortByName = (a: IFilterProductCatalogCategory, b: IFilterProductCatalogCategory) =>
    a.name.localeCompare(b.name, locale || 'ua');

  const handleGetTreeChildren = (items: IFilterProductCatalogCategory[]): ReactNode[] => {
    const arr: ReactNode[] = [];

    items.sort(sortByName).forEach((value) => {
      if (!value.id || value.hidden) {
        return;
      }
      const label = (
        <>
          {value.name !== '' ? value.name : 'Інша'}&nbsp;({value.productsCount})
        </>
      );

      const isHasChildren = value && value.items;
      const groupState = getGroupState(value);
      const isIndeterminate = isHasChildren ? groupState.isUnchecked && groupState.isChecked : false;

      const isSelected = attributeFilterValues.length > 0 && attributeFilterValues.indexOf(value.alias) > -1;
      const isChecked = isHasChildren ? groupState.isChecked || isSelected : isSelected;

      const treeLabel = (
        <Checkbox
          checked={Boolean(value.productsCount && isChecked)}
          disabled={!Boolean(value.productsCount)}
          label={label}
          onChange={handleChangeCheckbox(value)}
          indeterminate={isIndeterminate}
          name={value.name}
        />
      );

      if (isHasChildren) {
        return arr.push(
          <TreeItem key={value.id} label={treeLabel} expand={isChecked || isIndeterminate}>
            {handleGetTreeChildren(value.items)}
          </TreeItem>
        );
      }

      arr.push(<TreeItem key={value.id} label={treeLabel} />);
    });

    return arr;
  };

  const items =
    filterOptionsData.catalogCategory.length === 1 && filterOptionsData.catalogCategory[0].items.length
      ? filterOptionsData.catalogCategory[0].items
      : filterOptionsData.catalogCategory;

  if (attributeOptions) {
    attributeOptionNodes = handleGetTreeChildren(items);
  }
  const labelComp = (
    <FilterLabel
      classes={classes}
      label={labelSection}
      arraySelectedLast={arraySelectedLast}
      arraySelectedLength={arraySelectedLength}
      handleReset={handleResetCategoryFilters}
    />
  );

  const filteredItems = items
    .filter((c) => c.productsCount)
    .sort(sortByProductCount)
    .sort(sortByIsTopLevel)
    .slice(0, MAX_CATEGORIES_OPTIONS_SHOWN);

  const firstAttributeOptionNodes = handleGetTreeChildren(filteredItems);

  const toggleAllList = () => setAllList((state) => !state);

  return (
    <ExpandPanel defaultExpanded={true} label={labelComp} className={classes.paddedExpandPanel}>
      <>
        {!allList ? <Collapse in={!allList}>{firstAttributeOptionNodes}</Collapse> : null}
        <Collapse in={allList}>
          <div className={classes.expandedList}>
            {attributeOptionNodes.map((item) => {
              return item;
            })}
          </div>
        </Collapse>
      </>

      {attributeOptionNodes && attributeOptionNodes.length > MAX_CATEGORIES_OPTIONS_SHOWN ? (
        <div className={classes.collapseAllButton}>
          <UnderlinedButton
            onClick={toggleAllList}
            label={`${allList ? 'Згорнути' : 'Розгорнути'} (${
              attributeOptionNodes.length - firstAttributeOptionNodes.length
            })`}
          />
        </div>
      ) : null}
    </ExpandPanel>
  );
};

export const prepareFilterCategoriesStateChildren = (
  item: IFilterProductCatalogCategory,
  isChecked: boolean,
  filtersFast: IFilterProp[]
) => {
  const newFilterPropValue: IFilterProp = {
    rule: 'include',
    path: 'category',
    value: item.alias,
    label: item.name || item.alias
  };

  /** change only if has products */
  if (Boolean(item.productsCount)) {
    if (!isChecked) {
      _remove(filtersFast, newFilterPropValue);
    } else if (!_includes(filtersFast, newFilterPropValue)) {
      filtersFast.push(newFilterPropValue);
    }
  }

  item.items.forEach((subItem) => {
    if (subItem.items && subItem.items.length) {
      prepareFilterCategoriesStateChildren(subItem, isChecked, filtersFast);
    } else {
      const newFilterPropValue: IFilterProp = {
        rule: 'include',
        path: 'category',
        value: subItem.alias,
        label: subItem.name || subItem.alias
      };

      /** change only if has products */
      if (Boolean(subItem.productsCount)) {
        if (!isChecked) {
          _remove(filtersFast, newFilterPropValue);
        } else if (!_includes(filtersFast, newFilterPropValue)) {
          filtersFast.push(newFilterPropValue);
        }
      }
    }
  });

  return filtersFast;
};

export default CatalogCategory;
